import {
  OpsBookingSheetSchema,
  OpsCaseDocumentListSchema,
  OpsItemQueueSchema,
  OpsProofUrlSchema,
  OpsSellerScreenshotListSchema,
  OpsCaseItemListSchema,
  OpsAssigneeListSchema,
  OpsCaseListResponseSchema,
  OpsCaseRowSchema,
  OpsCaseSummarySchema,
} from '@utazzo/shared';

import type {
  OpsAmendmentInput,
  OpsApprovalDecisionInput,
  OpsBookingSheet,
  OpsCaseDocument,
  OpsDocumentUploadInput,
  OpsItemQueueRow,
  OpsMarkPaidInput,
  OpsProofUrl,
  OpsRequestFundInput,
  OpsResolveAmendmentInput,
  OpsCaseItem,
  OpsHoldItemInput,
  OpsIssueItemInput,
  OpsAssignee,
  OpsCaseListResponse,
  OpsCaseRow,
  OpsCaseSummary,
  OpsCaseView,
  OpsSellerScreenshot,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/** Operations queue of converted leads (apps/api modules/ops-cases). */

export interface OpsCaseListQuery {
  view: OpsCaseView;
  page: number;
  pageSize: number;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  search?: string;
  assignedToId?: string;
  leadId?: string;
}

/** Base64 file uploads (tickets, payment proof): 2 minutes instead of the default 30s. */
const UPLOAD_TIMEOUT_MS = 120_000;

export const listOpsCases = async (query: OpsCaseListQuery): Promise<OpsCaseListResponse> => {
  const { data } = await apiClient.get<unknown>('/ops-cases', { params: query });
  return OpsCaseListResponseSchema.parse(data);
};

export const fetchOpsCaseSummary = async (assignedToId?: string): Promise<OpsCaseSummary> => {
  const { data } = await apiClient.get<unknown>('/ops-cases/summary', {
    params: assignedToId ? { assignedToId } : {},
  });
  return OpsCaseSummarySchema.parse(data);
};

export const fetchOpsAssignees = async (): Promise<OpsAssignee[]> => {
  const { data } = await apiClient.get<unknown>('/ops-cases/assignees');
  return OpsAssigneeListSchema.parse(data);
};

const post = async (path: string, body?: unknown): Promise<OpsCaseRow> => {
  const { data } = await apiClient.post<unknown>(path, body);
  return OpsCaseRowSchema.parse(data);
};

export const openOpsCase = (id: string) => post(`/ops-cases/${id}/open`);
export const completeOpsCase = (id: string) => post(`/ops-cases/${id}/complete`);
export const reopenOpsCase = (id: string) => post(`/ops-cases/${id}/reopen`);
/** Admin only: every desk of the lead closes because it was booked outside the system. */
export const markOpsCaseAlreadyBooked = (id: string) => post(`/ops-cases/${id}/already-booked`);
export const cancelOpsCase = (id: string) => post(`/ops-cases/${id}/cancel`);
export const assignOpsCase = (id: string, userId: string | null) =>
  post(`/ops-cases/${id}/assign`, { userId });
/** Every desk of the lead moves to this Operations owner (managers only). */
export const setOpsCoordinator = (id: string, userId: string) =>
  post(`/ops-cases/${id}/coordinator`, { userId });
export const enqueueOpsCase = (leadId: string) => post('/ops-cases', { leadId });

// --- Per-item booking (hold / issue) ---

/** Full-page booking screen: case + trip + customer + travellers + items with booking facts. */
export const fetchOpsBookingSheet = async (id: string): Promise<OpsBookingSheet> => {
  const { data } = await apiClient.get<unknown>(`/ops-cases/${id}/booking-sheet`);
  return OpsBookingSheetSchema.parse(data);
};

export const listOpsCaseItems = async (id: string): Promise<OpsCaseItem[]> => {
  const { data } = await apiClient.get<unknown>(`/ops-cases/${id}/items`);
  return OpsCaseItemListSchema.parse(data);
};

export const holdOpsCaseItem = async (
  id: string,
  componentId: string,
  input: OpsHoldItemInput,
): Promise<OpsCaseItem[]> => {
  const { data } = await apiClient.put<unknown>(
    `/ops-cases/${id}/items/${componentId}/hold`,
    input,
  );
  return OpsCaseItemListSchema.parse(data);
};

export const issueOpsCaseItem = async (
  id: string,
  componentId: string,
  input: OpsIssueItemInput,
): Promise<OpsCaseItem[]> => {
  // Carries the tickets as base64; a slow API + S3 can take well past the
  // default 30s, and a client timeout here leaves the item issued server-side
  // with the operator thinking it failed.
  const { data } = await apiClient.post<unknown>(
    `/ops-cases/${id}/items/${componentId}/issue`,
    input,
    { timeout: UPLOAD_TIMEOUT_MS },
  );
  return OpsCaseItemListSchema.parse(data);
};

export const resetOpsCaseItem = async (id: string, componentId: string): Promise<OpsCaseItem[]> => {
  const { data } = await apiClient.post<unknown>(`/ops-cases/${id}/items/${componentId}/reset`);
  return OpsCaseItemListSchema.parse(data);
};

// --- Fund flow: ask for fund, approvals, amendment ---

export const requestOpsFund = async (
  id: string,
  componentId: string,
  input: OpsRequestFundInput,
): Promise<OpsCaseItem[]> => {
  const { data } = await apiClient.post<unknown>(
    `/ops-cases/${id}/items/${componentId}/request-fund`,
    input,
  );
  return OpsCaseItemListSchema.parse(data);
};

export const requestOpsAmendment = async (
  id: string,
  componentId: string,
  input: OpsAmendmentInput,
): Promise<OpsCaseItem[]> => {
  const { data } = await apiClient.post<unknown>(
    `/ops-cases/${id}/items/${componentId}/amendment`,
    input,
  );
  return OpsCaseItemListSchema.parse(data);
};

export const listOpsApprovals = async (): Promise<OpsItemQueueRow[]> => {
  const { data } = await apiClient.get<unknown>('/ops-cases/approvals');
  return OpsItemQueueSchema.parse(data);
};

export const decideOpsApproval = async (
  id: string,
  componentId: string,
  input: OpsApprovalDecisionInput,
): Promise<OpsItemQueueRow[]> => {
  const { data } = await apiClient.post<unknown>(
    `/ops-cases/${id}/items/${componentId}/approval`,
    input,
  );
  return OpsItemQueueSchema.parse(data);
};

export const fetchOpsPaymentProof = async (
  id: string,
  componentId: string,
  instalmentId?: string,
): Promise<OpsProofUrl> => {
  const { data } = await apiClient.get<unknown>(
    `/ops-cases/${id}/items/${componentId}/payment-proof`,
    { params: instalmentId ? { instalmentId } : {} },
  );
  return OpsProofUrlSchema.parse(data);
};

// --- Accounts (ops.fund_requests) ---

export const listFundRequests = async (view: 'requested' | 'paid'): Promise<OpsItemQueueRow[]> => {
  const { data } = await apiClient.get<unknown>('/ops-fund-requests', { params: { view } });
  return OpsItemQueueSchema.parse(data);
};

export const markFundPaid = async (
  caseId: string,
  componentId: string,
  input: OpsMarkPaidInput,
): Promise<OpsItemQueueRow[]> => {
  const { data } = await apiClient.post<unknown>(
    `/ops-fund-requests/${caseId}/${componentId}/paid`,
    input,
    { timeout: UPLOAD_TIMEOUT_MS },
  );
  return OpsItemQueueSchema.parse(data);
};

export const fetchFundProof = async (
  caseId: string,
  componentId: string,
  instalmentId?: string,
): Promise<OpsProofUrl> => {
  const { data } = await apiClient.get<unknown>(
    `/ops-fund-requests/${caseId}/${componentId}/proof`,
    { params: instalmentId ? { instalmentId } : {} },
  );
  return OpsProofUrlSchema.parse(data);
};

// --- Seller amendments (ops.leads) ---

export const listOpsAmendments = async (leadId?: string): Promise<OpsItemQueueRow[]> => {
  const { data } = await apiClient.get<unknown>('/ops-amendments', {
    params: leadId ? { leadId } : {},
  });
  return OpsItemQueueSchema.parse(data);
};

export const resolveOpsAmendment = async (
  caseId: string,
  componentId: string,
  input: OpsResolveAmendmentInput,
): Promise<OpsItemQueueRow[]> => {
  const { data } = await apiClient.post<unknown>(
    `/ops-amendments/${caseId}/${componentId}/resolve`,
    input,
  );
  return OpsItemQueueSchema.parse(data);
};

// --- Documents on the case: payment proofs, tickets, vouchers ---

export const listOpsCaseDocuments = async (caseId: string): Promise<OpsCaseDocument[]> => {
  const { data } = await apiClient.get<unknown>(`/ops-cases/${caseId}/documents`);
  return OpsCaseDocumentListSchema.parse(data);
};

export const uploadOpsCaseDocument = async (
  caseId: string,
  input: OpsDocumentUploadInput,
): Promise<OpsCaseDocument[]> => {
  const { data } = await apiClient.post<unknown>(`/ops-cases/${caseId}/documents`, input, {
    timeout: UPLOAD_TIMEOUT_MS,
  });
  return OpsCaseDocumentListSchema.parse(data);
};

export const deleteOpsCaseDocument = async (
  caseId: string,
  docId: string,
): Promise<OpsCaseDocument[]> => {
  const { data } = await apiClient.delete<unknown>(`/ops-cases/${caseId}/documents/${docId}`);
  return OpsCaseDocumentListSchema.parse(data);
};

export const fetchOpsCaseDocumentUrl = async (
  caseId: string,
  docId: string,
): Promise<OpsProofUrl> => {
  const { data } = await apiClient.get<unknown>(`/ops-cases/${caseId}/documents/${docId}/url`);
  return OpsProofUrlSchema.parse(data);
};

/** The seller's screenshots from the lead's Screenshots tab (read-only for ops). */
export const listOpsSellerScreenshots = async (caseId: string): Promise<OpsSellerScreenshot[]> => {
  const { data } = await apiClient.get<unknown>(`/ops-cases/${caseId}/seller-screenshots`);
  return OpsSellerScreenshotListSchema.parse(data);
};

export const fetchOpsSellerScreenshotUrl = async (
  caseId: string,
  screenshotId: string,
): Promise<OpsProofUrl> => {
  const { data } = await apiClient.get<unknown>(
    `/ops-cases/${caseId}/seller-screenshots/${screenshotId}/url`,
  );
  return OpsProofUrlSchema.parse(data);
};

/** Lead page (ops.leads): the seller's read-only view of the same files. */
export const listLeadOpsDocuments = async (leadId: string): Promise<OpsCaseDocument[]> => {
  const { data } = await apiClient.get<unknown>('/ops-documents', { params: { leadId } });
  return OpsCaseDocumentListSchema.parse(data);
};

export const fetchLeadOpsDocumentUrl = async (
  caseId: string,
  docId: string,
): Promise<OpsProofUrl> => {
  const { data } = await apiClient.get<unknown>(`/ops-documents/${caseId}/${docId}/url`);
  return OpsProofUrlSchema.parse(data);
};
